import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Spinner, Center, useToast, Box, Text, Button } from "@chakra-ui/react";
import DocumentEditorView from "./DocumentEditorView";
import { useAuth } from "../AuthContext";
import { useAuthenticatedRequest } from "../hooks/useAuthenticatedRequest";
import { getDeal } from "../services/MrcaiAPI";

/** Detect an "empty" tiptap doc */
const isEmptyDoc = (raw) => {
  if (!raw) return true;
  if (typeof raw === "string") {
    const t = raw.trim();
    return t.length === 0 || t === "{}" || t === "null";
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return Object.keys(raw).length === 0;
  }
  return false;
};

/** Parse content that may already be an object or a JSON string. */
const parseTiptapContent = (content) => {
  if (!content) return null;
  if (typeof content === "object") return content;
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
};

/**
 * Pick which document to load from the contract.documents array.
 * Priority:
 *  0) if documentId provided (route or querystring), use that
 *  1) status === 'active'
 *  2) newest by createdDate
 *  3) first item
 */
const pickContractDocument = (documents = [], preferredId) => {
  if (!Array.isArray(documents) || documents.length === 0) return null;

  if (preferredId) {
    // ?doc= may be the deal's document entry id or the document's own id
    const byId = documents.find((d) => d?.id === preferredId || d?.documentRepositoryId === preferredId);
    if (byId) return byId;
  }

  const active =
    documents.find((d) => String(d?.status || "").toLowerCase() === "active") ||
    null;
  if (active) return active;

  const newest =
    [...documents]
      .filter((d) => !!d?.createdDate)
      .sort(
        (a, b) =>
          new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
      )[0] || null;

  return newest || documents[0] || null;
};

export default function DocumentEditorLoader({ userRole }) {
  const { user } = useAuth();
  const { get } = useAuthenticatedRequest();
  const { dealId, productCode, documentId: documentIdFromRoute } = useParams();
  const [searchParams] = useSearchParams();
  const documentIdFromQuery = searchParams.get("doc");
  const toast = useToast();

  const preferredDocId = documentIdFromRoute || documentIdFromQuery || null;

  // Extract user roles from platformUser
  const userRoles = user?.platformUser?.roles || [];

  const [contract, setContract] = useState(null);
  const [docFromRepo, setDocFromRepo] = useState(null);
  const [repoCodified, setRepoCodified] = useState([]);
  const [docMeta, setDocMeta] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Detect if we're in template mode (dealId === "template")
  const isTemplateMode = dealId === "template";

  // 1) Load contract (skip in template mode)
  useEffect(() => {
    if (isTemplateMode) {
      // For template mode, create a fake contract object
      setContract({
        _id: "template",
        productCode: productCode,
        documents: documentIdFromQuery
          ? [
              {
                id: crypto.randomUUID(),
                documentRepositoryId: documentIdFromQuery,
                status: "template",
              },
            ]
          : [],
      });
      return;
    }

    if (!dealId || !productCode) return;

    const ac = new AbortController();
    (async () => {
      try {
        const data = await getDeal(dealId);

        const merged = {
          _id: data?._id ?? dealId,
          productCode: data?.productCode ?? productCode,
          ...data,
        };
        setContract(merged);
      } catch (err) {
        if (err.name !== "AbortError") {
          setContract({ _id: dealId, productCode, documents: [] });
        }
      }
    })();
    return () => ac.abort();
  }, [dealId, productCode, isTemplateMode, documentIdFromQuery]);

  // 2) Load the chosen document from DocumentsAPI
  useEffect(() => {
    if (!contract) {
      return;
    }

    const chosen = pickContractDocument(contract.documents, preferredDocId);
    const repoId = chosen?.documentRepositoryId;

    // Reset previous state
    setDocFromRepo(null);
    setDocMeta(chosen || null);

    if (!repoId) {
      return; // no documents to fetch
    }

    const ac = new AbortController();
    setDocLoading(true);
    (async () => {
      try {
        const res = await get(`https://documents.poc.mrc.ai/api/documents/${repoId}`);
        const docJson = res.data;

        // Expect { content: TipTapJson | string, ... }
        const parsed = parseTiptapContent(docJson?.content);
        if (parsed) {
          setDocFromRepo(parsed);
        } else {
          setDocFromRepo(null);
        }

        setRepoCodified(Array.isArray(docJson?.codifiedContents) ? docJson.codifiedContents : []);

        setDocMeta((prev) => ({
          ...prev,
          _repoFetch: {
            id: docJson?._id,
            status: docJson?.status,
            documentRepositoryId: docJson?.documentRepositoryId,
            definitionId: docJson?.definitionId,
            createdDate: docJson?.createdDate,
            codifyCollection: docJson?.codifyCollection,
            dataStandard: docJson?.dataStandard,
            dataType: docJson?.dataType,
            model: docJson?.model,
            name: docJson?.name,
            productCode: docJson?.productCode,
            isTemplate: docJson?.isTemplate,
            codifySpecifications: docJson?.codifySpecifications,
            organisationOwnerId: docJson?.organisationOwnerId,
          },
        }));
      } catch (err) {
        if (err.name !== "AbortError") {
          setDocFromRepo(null);
          setLoadError(err);

          // Check if it's a 401 unauthorized error
          if (err.response?.status === 401) {
            toast({
              title: "Session Expired",
              description: "Your session has expired. Please refresh the page to log in again.",
              status: "error",
              duration: 10000,
              isClosable: true,
            });
          } else {
            toast({
              title: "Failed to Load Document",
              description: err.message || "Unable to load the document. Please try again.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          }
        }
      } finally {
        setDocLoading(false);
      }
    })();

    return () => ac.abort();
  }, [contract, preferredDocId]);

  // 3) Decide effective doc
const effectiveDoc = useMemo(() => {
  if (docFromRepo && !isEmptyDoc(docFromRepo)) return docFromRepo;
}, [docFromRepo]);


  // 5) Loading gate
  const stillBootstrapping = !contract || docLoading;

  if (stillBootstrapping) {
    return (
      <Center height="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  // 6) Error state
  if (loadError || !effectiveDoc) {
    return (
      <Center height="100vh">
        <Box textAlign="center" p={8}>
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            {loadError?.response?.status === 401
              ? "Session Expired"
              : "Unable to Load Document"}
          </Text>
          <Text color="gray.600" mb={6}>
            {loadError?.response?.status === 401
              ? "Your session has expired. Please refresh the page to log in again."
              : "The document could not be loaded. Please try again."}
          </Text>
          <Button colorScheme="blue" onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        </Box>
      </Center>
    );
  }

  // 7) Render editor
  return (
    <DocumentEditorView
      tiptapDocument={effectiveDoc}
      userRole={userRole}
      userRoles={userRoles}
      contract={contract}
      documentMeta={{
        documentId: docMeta?.id || null,
        documentRepositoryId: docMeta?.documentRepositoryId || null,
        definitionId: docMeta?.definitionId || docMeta?._repoFetch?.definitionId || null,
        status: docMeta?.status || null,
        createdDate: docMeta?.createdDate || docMeta?._repoFetch?.createdDate || null,
        codifyCollection: docMeta?.codifyCollection || docMeta?._repoFetch?.codifyCollection || null,
        dataStandard: docMeta?.dataStandard || docMeta?._repoFetch?.dataStandard || null,
        dataType: docMeta?.dataType || docMeta?._repoFetch?.dataType || null,
        model: docMeta?.model || docMeta?._repoFetch?.model || null,
        name: docMeta?.name || docMeta?._repoFetch?.name || null,
        productCode: docMeta?.productCode || docMeta?._repoFetch?.productCode || contract?.productCode || null,
        isTemplate: docMeta?.isTemplate ?? docMeta?._repoFetch?.isTemplate ?? null,
        codifySpecifications: docMeta?.codifySpecifications || docMeta?._repoFetch?.codifySpecifications || [],
        organisationOwnerId: docMeta?.organisationOwnerId || docMeta?._repoFetch?.organisationOwnerId || null,
      }}
      initialCodifiedContents={repoCodified}
    />
  );
}
